import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AppHeader } from "../components/AppHeader";
import { ContextBar } from "../components/ContextBar";
import { EmptyState, MoneyStat } from "../components/MoneyStat";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { api, erroMsg } from "../lib/api";
import { brl, parsePctInput, pct, toPctInput } from "../lib/format";
import { useCart } from "../context/CartContext";
import { useMeta } from "../context/MetaContext";
import {
  FileDown,
  Loader2,
  Minus,
  Pencil,
  Percent,
  Plus,
  Save,
  ShoppingCart,
  Trash2,
  X,
} from "lucide-react";

/** Campo de desconto por item, com trava no máximo do produto */
const DescontoItem = ({ item, onChange }) => {
  const [texto, setTexto] = useState(toPctInput(item.desconto, 2));

  React.useEffect(() => {
    setTexto(toPctInput(item.desconto, 2));
  }, [item.desconto]);

  const max = Number(item.desconto_maximo) || 0;
  const digitado = parsePctInput(texto);
  const acima = digitado > max + 1e-9;

  return (
    <div className="space-y-1">
      <div className="relative">
        <Input
          inputMode="decimal"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onBlur={() => {
            const f = Math.min(Math.max(parsePctInput(texto), 0), max);
            onChange(f);
            setTexto(toPctInput(f, 2));
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
          }}
          className={`h-10 w-[92px] pr-6 text-right num ${
            acima ? "border-destructive focus-visible:ring-destructive" : ""
          }`}
          data-testid={`orcamento-item-desconto-input-${item.codigo}`}
        />
        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-slate-400">
          %
        </span>
      </div>
      <div
        className={`text-[10.5px] ${acima ? "font-medium text-destructive" : "text-slate-500"}`}
        data-testid={`orcamento-item-desconto-max-${item.codigo}`}
      >
        {acima ? `Máx. ${pct(max)}` : `máx. ${pct(max)}`}
      </div>
    </div>
  );
};

export default function Orcamento() {
  const {
    itens,
    atualizarItem,
    aplicarDescontoEmTodos,
    remover,
    limpar,
    calculo,
    calculando,
    erroCalculo,
    uf,
    enquadramento,
    cliente,
    setCliente,
    editandoId,
    editandoNumero,
    sairModoEdicao,
  } = useCart();
  const { ufNome, enqNome } = useMeta();
  const navigate = useNavigate();

  const [condicoes, setCondicoes] = useState([]);

  React.useEffect(() => {
    api.get("/payment-terms").then((r) => setCondicoes(r.data.itens)).catch(() => {});
  }, []);
  const [salvando, setSalvando] = useState(false);
  const [salvo, setSalvo] = useState(null);
  const [confirmarLimpar, setConfirmarLimpar] = useState(false);
  const [massa, setMassa] = useState({ aberto: false, valor: "" });

  const totais = calculo?.totais;
  const itensCalc = calculo?.itens || [];

  const salvar = async (navegarDepois = true) => {
    if (!itens.length) return;
    setSalvando(true);
    const payload = {
      cliente_nome: cliente.nome,
      cliente_cnpj: cliente.cnpj,
      cliente_contato: cliente.contato,
      observacoes: cliente.observacoes,
      condicao_pagamento: cliente.condicao_pagamento,
      prazo_entrega: cliente.prazo_entrega,
      uf,
      enquadramento,
      desconto: 0,
      itens: itens.map((i) => ({
        produto_id: i.produto_id,
        quantidade: Number(i.quantidade) || 1,
        desconto: Number(i.desconto) || 0,
      })),
    };
    try {
      let r;
      if (editandoId) {
        r = await api.put(`/quotes/${editandoId}`, payload);
        toast.success(`Orçamento ${r.data.numero} atualizado com sucesso`);
      } else {
        r = await api.post("/quotes", payload);
        toast.success(`Orçamento ${r.data.numero} salvo com sucesso`);
      }
      setSalvo(r.data);
      if (navegarDepois) {
        sairModoEdicao();
        navigate("/orcamentos");
      }
      return r.data;
    } catch (e) {
      toast.error(erroMsg(e, "Não foi possível salvar o orçamento."));
      return null;
    } finally {
      setSalvando(false);
    }
  };

  const abrirPdf = async () => {
    let q = salvo;
    if (!q) q = await salvar(false);
    if (!q) return;
    try {
      const r = await api.get(`/quotes/${q.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) {
      toast.error("Não foi possível gerar o PDF.");
    }
  };

  const descontoTotal =
    totais && totais.total_liquido != null
      ? itensCalc.reduce((acc, i) => acc + i.desconto_valor * i.quantidade, 0)
      : 0;

  return (
    <div className="min-h-screen bg-slate-50 pb-10">
      <AppHeader />

      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-3 py-4 sm:px-6">
          <h1 className="font-display text-[20px] font-bold tracking-tight text-slate-900">
            Orçamento
          </h1>
          <p className="text-[12.5px] text-slate-600">
            {ufNome(uf)} • {enqNome(enquadramento)} • desconto negociado item por item
          </p>
          {editandoId && (
            <div
              className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
              data-testid="orcamento-modo-edicao-banner"
            >
              <span className="flex items-center gap-1.5 text-[12.5px] text-amber-900">
                <Pencil className="h-3.5 w-3.5 shrink-0" />
                Editando o orçamento <b>{editandoNumero}</b> — salvar irá atualizar este
                orçamento existente.
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-amber-900 hover:bg-amber-100"
                onClick={() => {
                  sairModoEdicao();
                  setSalvo(null);
                  navigate("/orcamentos");
                }}
                data-testid="orcamento-cancelar-edicao-button"
              >
                <X className="mr-1 h-3.5 w-3.5" /> Cancelar edição
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-3 py-5 sm:px-6">
        {itens.length === 0 ? (
          <EmptyState
            icone={ShoppingCart}
            titulo="Seu orçamento está vazio"
            descricao="Busque produtos na consulta e adicione ao orçamento. Aqui você define o desconto de cada item separadamente."
            acao={
              <Button onClick={() => navigate("/consulta")} data-testid="orcamento-ir-consulta">
                Ir para a consulta
              </Button>
            }
          />
        ) : (
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
            <div className="space-y-4">
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Contexto fiscal do orçamento
                </div>
                <ContextBar />
              </div>

              {erroCalculo && (
                <div className="rounded-xl border border-destructive/30 bg-red-50 px-4 py-3 text-sm text-destructive">
                  {erroCalculo}
                </div>
              )}

              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
                  <div className="font-display text-sm font-semibold text-slate-900">
                    Itens ({itens.length})
                  </div>
                  <div className="flex gap-1.5">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setMassa({ aberto: true, valor: "" })}
                      data-testid="orcamento-desconto-todos-button"
                    >
                      <Percent className="mr-1.5 h-4 w-4" /> Mesmo desconto p/ todos
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:bg-red-50"
                      onClick={() => setConfirmarLimpar(true)}
                      data-testid="orcamento-limpar-button"
                    >
                      <Trash2 className="mr-1.5 h-4 w-4" /> Limpar
                    </Button>
                  </div>
                </div>

                <ul className="divide-y divide-slate-100">
                  {itens.map((it) => {
                    const calc = itensCalc.find((c) => c.produto_id === it.produto_id);
                    return (
                      <li key={it.produto_id} className="p-4" data-testid={`orcamento-item-${it.codigo}`}>
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="font-display text-[14.5px] font-semibold leading-snug text-slate-900">
                              {it.descricao_produto}
                            </div>
                            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11.5px] text-slate-500">
                              <span className="font-mono-num">{it.codigo}</span>
                              <span className="text-slate-300">•</span>
                              <span className="font-mono-num">NCM {it.ncm}</span>
                              <span className="text-slate-300">•</span>
                              <span>{it.unidade}</span>
                            </div>
                          </div>

                          <div className="flex items-end gap-3">
                            <div className="space-y-1">
                              <div className="text-[10.5px] font-semibold uppercase tracking-wide text-slate-500">
                                Desconto
                              </div>
                              <DescontoItem
                                item={it}
                                onChange={(f) => atualizarItem(it.produto_id, { desconto: f })}
                              />
                            </div>

                            <div className="space-y-1">
                              <div className="text-[10.5px] font-semibold uppercase tracking-wide text-slate-500">
                                Quantidade
                              </div>
                              <div className="flex items-center gap-1.5">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="h-10 w-10"
                                  onClick={() =>
                                    atualizarItem(it.produto_id, {
                                      quantidade: Math.max(1, Number(it.quantidade) - 1),
                                    })
                                  }
                                  data-testid={`orcamento-menos-${it.codigo}`}
                                >
                                  <Minus className="h-4 w-4" />
                                </Button>
                                <Input
                                  type="number"
                                  min={1}
                                  inputMode="numeric"
                                  value={it.quantidade}
                                  onChange={(e) =>
                                    atualizarItem(it.produto_id, {
                                      quantidade: e.target.value === "" ? "" : Number(e.target.value),
                                    })
                                  }
                                  onBlur={(e) => {
                                    if (!e.target.value || Number(e.target.value) < 1)
                                      atualizarItem(it.produto_id, { quantidade: 1 });
                                  }}
                                  className="h-10 w-20 text-center num"
                                  data-testid={`orcamento-item-quantidade-input-${it.codigo}`}
                                />
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="h-10 w-10"
                                  onClick={() =>
                                    atualizarItem(it.produto_id, {
                                      quantidade: Number(it.quantidade) + 1,
                                    })
                                  }
                                  data-testid={`orcamento-mais-${it.codigo}`}
                                >
                                  <Plus className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>

                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-10 w-10 text-slate-400 hover:bg-red-50 hover:text-destructive"
                              onClick={() => remover(it.produto_id)}
                              data-testid={`orcamento-remover-${it.codigo}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        {calc && (
                          <div className="mt-3 grid grid-cols-2 gap-2 rounded-lg bg-slate-50 px-3 py-2 text-[12px] sm:grid-cols-6">
                            <div>
                              <div className="text-slate-500">Tabela</div>
                              <div className="num font-medium text-slate-800">
                                {brl(calc.valor_tabela)}
                              </div>
                            </div>
                            <div>
                              <div className="text-slate-500">Desconto ({pct(calc.desconto_pct)})</div>
                              <div className="num font-medium text-emerald-700">
                                − {brl(calc.desconto_valor)}
                              </div>
                            </div>
                            <div>
                              <div className="text-slate-500">Líquido</div>
                              <div className="num font-medium text-slate-800">
                                {brl(calc.valor_liquido)}
                              </div>
                            </div>
                            <div>
                              <div className="text-slate-500">IPI ({pct(calc.ipi_pct)})</div>
                              <div className="num font-medium text-slate-800">{brl(calc.ipi_valor)}</div>
                            </div>
                            <div>
                              <div className="text-slate-500">ST ({pct(calc.aliquota_st)})</div>
                              <div className="num font-medium text-slate-800">{brl(calc.st_valor)}</div>
                            </div>
                            <div className="text-right sm:text-left">
                              <div className="text-slate-500">
                                Total ({brl(calc.total_unitario)}/{it.unidade})
                              </div>
                              <div
                                className="num font-display font-bold text-slate-900"
                                data-testid={`orcamento-total-item-${it.codigo}`}
                              >
                                {brl(calc.total_item)}
                              </div>
                            </div>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="mb-3 font-display text-sm font-semibold text-slate-900">
                  Dados do cliente
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="cnome">Nome / Razão social</Label>
                    <Input
                      id="cnome"
                      value={cliente.nome}
                      onChange={(e) => setCliente({ ...cliente, nome: e.target.value })}
                      placeholder="Ex.: Hidráulica Central LTDA"
                      className="h-11"
                      data-testid="orcamento-nome-cliente-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="ccnpj">CNPJ</Label>
                    <Input
                      id="ccnpj"
                      value={cliente.cnpj}
                      onChange={(e) => setCliente({ ...cliente, cnpj: e.target.value })}
                      placeholder="00.000.000/0000-00"
                      className="h-11"
                      data-testid="orcamento-cnpj-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="ccontato">Contato</Label>
                    <Input
                      id="ccontato"
                      value={cliente.contato}
                      onChange={(e) => setCliente({ ...cliente, contato: e.target.value })}
                      placeholder="Telefone ou e-mail"
                      className="h-11"
                      data-testid="orcamento-contato-input"
                    />
                  </div>
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Condição de pagamento</Label>
                    <Select
                      value={cliente.condicao_pagamento}
                      onValueChange={(v) => setCliente({ ...cliente, condicao_pagamento: v })}
                    >
                      <SelectTrigger className="h-11" data-testid="orcamento-condicao-pagamento-select">
                        <SelectValue placeholder="Selecione a condição" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {condicoes.map((c) => (
                          <SelectItem key={c.id} value={c.nome}>
                            {c.nome}
                            {c.descricao ? ` — ${c.descricao}` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="prazo">Prazo de entrega</Label>
                    <Input
                      id="prazo"
                      value={cliente.prazo_entrega}
                      onChange={(e) => setCliente({ ...cliente, prazo_entrega: e.target.value })}
                      placeholder="Ex.: 15 dias úteis após a confirmação"
                      className="h-11"
                      data-testid="orcamento-prazo-entrega-input"
                    />
                  </div>
                </div>
                <div className="mt-3 space-y-1.5">
                  <Label htmlFor="cobs">Observações</Label>
                  <Textarea
                    id="cobs"
                    value={cliente.observacoes}
                    onChange={(e) => setCliente({ ...cliente, observacoes: e.target.value })}
                    placeholder="Prazo de entrega, condições de pagamento, frete…"
                    rows={3}
                    data-testid="orcamento-observacoes-input"
                  />
                </div>
              </div>
            </div>

            {/* resumo */}
            <div className="h-fit space-y-3 lg:sticky lg:top-24">
              <div
                className="rounded-xl border border-slate-200 bg-white p-4"
                data-testid="orcamento-totais-resumo"
              >
                <div className="mb-3 flex items-center justify-between">
                  <div className="font-display text-sm font-semibold text-slate-900">Totais</div>
                  {calculando && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
                </div>
                <div className="space-y-2">
                  {descontoTotal > 0 && (
                    <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                      <span className="text-[11.5px] font-semibold uppercase tracking-wide text-emerald-800">
                        Desconto concedido
                      </span>
                      <span className="num font-display text-[16px] font-bold text-emerald-800">
                        − {brl(descontoTotal)}
                      </span>
                    </div>
                  )}
                  <MoneyStat
                    label="Total líquido (com descontos)"
                    valor={brl(totais?.total_liquido)}
                    testid="total-liquido"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <MoneyStat label="IPI" valor={brl(totais?.total_ipi)} testid="total-ipi" />
                    <MoneyStat label="ICMS-ST / DIFAL" valor={brl(totais?.total_st)} testid="total-st" />
                  </div>
                  <MoneyStat
                    label="Total geral do orçamento"
                    valor={brl(totais?.total_geral)}
                    destaque
                    sub={`${totais?.quantidade_itens || 0} itens • ${
                      totais?.quantidade_total || 0
                    } unidades`}
                    testid="total-geral"
                  />
                </div>

                <div className="mt-4 space-y-2">
                  <Button
                    className="h-11 w-full"
                    onClick={() => salvar()}
                    disabled={salvando}
                    data-testid="orcamento-salvar-button"
                  >
                    {salvando ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="mr-2 h-4 w-4" />
                    )}
                    {editandoId
                      ? salvo
                        ? `Alterações salvas (${salvo.numero})`
                        : "Salvar alterações"
                      : salvo
                      ? `Salvo (${salvo.numero}) — salvar novo`
                      : "Salvar orçamento"}
                  </Button>
                  <Button
                    variant="outline"
                    className="h-11 w-full"
                    onClick={abrirPdf}
                    data-testid="orcamento-gerar-pdf-button"
                  >
                    <FileDown className="mr-1.5 h-4 w-4" /> PDF
                  </Button>
                </div>

                <p className="mt-3 text-[11px] leading-relaxed text-slate-500">
                  Validade da proposta: <b>10 dias úteis</b>. Cada item respeita o desconto máximo do próprio produto. O ICMS-ST/DIFAL incide
                  sobre o valor líquido somado ao IPI.
                </p>
              </div>

              <Button
                variant="outline"
                className="h-11 w-full"
                onClick={() => navigate("/consulta")}
                data-testid="orcamento-adicionar-mais"
              >
                <Plus className="mr-1.5 h-4 w-4" /> Adicionar mais produtos
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* aplicar desconto em todos */}
      <Dialog open={massa.aberto} onOpenChange={(o) => setMassa((m) => ({ ...m, aberto: o }))}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Mesmo desconto para todos os itens</DialogTitle>
            <DialogDescription>
              Atalho para começar a negociação. Cada item recebe no máximo o desconto permitido
              para ele — depois você ajusta item por item.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Desconto (%)</Label>
            <Input
              inputMode="decimal"
              value={massa.valor}
              onChange={(e) => setMassa((m) => ({ ...m, valor: e.target.value }))}
              placeholder="Ex.: 15"
              className="h-11 num"
              data-testid="orcamento-desconto-todos-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMassa({ aberto: false, valor: "" })}>
              Cancelar
            </Button>
            <Button
              onClick={() => {
                aplicarDescontoEmTodos(parsePctInput(massa.valor));
                setMassa({ aberto: false, valor: "" });
                toast.success("Desconto aplicado a todos os itens (respeitando o máximo de cada um)");
              }}
              data-testid="orcamento-desconto-todos-aplicar"
            >
              Aplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmarLimpar} onOpenChange={setConfirmarLimpar}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Limpar o orçamento?</AlertDialogTitle>
            <AlertDialogDescription>
              Todos os itens serão removidos. Orçamentos já salvos não são afetados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="orcamento-limpar-cancelar">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                limpar();
                setSalvo(null);
                toast.success("Orçamento limpo");
              }}
              data-testid="orcamento-limpar-confirmar"
            >
              Limpar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
